import React, { useState, useEffect, useCallback } from 'react'
import api from '../api/client'
import { CheckCircle, XCircle, Clock, Calendar, MapPin, Users, User, Mail, AlertCircle, RefreshCw, Eye, X } from 'lucide-react'
import { StatusBadge, Tag } from '@rallyshift/design-system'
import { formatDayDateToLocal, formatTimeRangeToLocal } from '../utils/timezone'
import { statusLabel } from '../utils/status'
import { useAuth } from '../contexts/AuthContext'

interface TodoItem {
  id: string
  shift: {
    id: string
    title: string
    description?: string
    start_time: string
    end_time: string
    location: string
    max_volunteers: number
    current_volunteers: number
    status: string
    shift_type: string
    event?: {
      id: string
      title: string
    }
  }
  assignment: {
    id: string
    shift_id: string
    volunteer_id: string
    status: string
    assigned_at: string
  }
  volunteer: {
    first_name: string
    last_name: string
    email: string
    avatar_url?: string
  }
}

const AdminTodoPage: React.FC = () => {
  const { isAdmin } = useAuth()
  const [todoItems, setTodoItems] = useState<TodoItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedVolunteer, setSelectedVolunteer] = useState<any>(null)
  const [volunteerDetailsLoading, setVolunteerDetailsLoading] = useState(false)

  const fetchVolunteerDetails = async (volunteerId: string) => {
    try {
      setVolunteerDetailsLoading(true)
      const profileData = await api.profiles.get(volunteerId)
      let volunteerData = null
      try {
        const list = await api.volunteerMetadata.list()
        volunteerData = list?.find((v: any) => v.id === volunteerId) || null
      } catch (e) { /* optional */ }
      const shiftHistory = await api.shifts.getAssignmentsByVolunteer(volunteerId)
      setSelectedVolunteer({
        ...volunteerData,
        profiles: profileData,
        shiftHistory: shiftHistory || []
      })
      setIsDetailsModalOpen(true)
    } catch (err: any) {
      console.error('Error fetching volunteer details:', err)
      setError(err.message || 'Failed to fetch volunteer details')
    } finally {
      setVolunteerDetailsLoading(false)
    }
  }

  const fetchTodoItems = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const data = await api.shifts.getPendingAssignments()
      const items: TodoItem[] = (data || []).map((a: any) => ({
        id: a.id,
        shift: a.shift,
        assignment: { id: a.id, shift_id: a.shift_id, volunteer_id: a.volunteer_id, status: a.status, assigned_at: a.assigned_at },
        volunteer: a.volunteer || { first_name: '', last_name: '', email: '' }
      }))
      setTodoItems(items)
    } catch (err: any) {
      console.error('Error fetching todo items:', err)
      setError(err.message || 'Failed to fetch todo items')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin) {
      fetchTodoItems()
    }
  }, [isAdmin, fetchTodoItems])

  const handleConfirmAssignment = async (assignmentId: string) => {
    try {
      setProcessingIds(prev => new Set(prev).add(assignmentId))

      // Update assignment status to confirmed
      await api.shifts.updateAssignment(assignmentId, { status: 'confirmed' })

      const assignment = todoItems.find(item => item.id === assignmentId)
      if (assignment) {
        try {
          await api.notifications.send('shift_assigned', {
            volunteerEmail: assignment.volunteer.email,
            volunteerFirstName: assignment.volunteer.first_name,
            volunteerLastName: assignment.volunteer.last_name,
            shiftTitle: assignment.shift.title,
            shiftDescription: assignment.shift.description,
            shiftStartTime: assignment.shift.start_time,
            shiftEndTime: assignment.shift.end_time,
            shiftLocation: assignment.shift.location,
            eventTitle: assignment.shift.event?.title,
            assignedAt: assignment.assignment.assigned_at,
            formattedStartTime: new Date(assignment.shift.start_time).toLocaleString('nl-NL'),
            formattedEndTime: new Date(assignment.shift.end_time).toLocaleString('nl-NL'),
            formattedAssignedDate: new Date(assignment.assignment.assigned_at).toLocaleDateString('nl-NL')
          })
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError)
        }
      }

      setTodoItems(prev => prev.filter(item => item.id !== assignmentId))
    } catch (err: any) {
      console.error('Error confirming assignment:', err)
      setError(err.message || 'Failed to confirm assignment')
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(assignmentId)
        return newSet
      })
    }
  }

  const handleCancelAssignment = async (assignmentId: string) => {
    try {
      setProcessingIds(prev => new Set(prev).add(assignmentId))

      // Update assignment status to cancelled
      await api.shifts.updateAssignment(assignmentId, { status: 'cancelled' })

      setTodoItems(prev => prev.filter(item => item.id !== assignmentId))
    } catch (err: any) {
      console.error('Error cancelling assignment:', err)
      setError(err.message || 'Failed to cancel assignment')
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(assignmentId)
        return newSet
      })
    }
  }

  const getUrgencyLevel = (assignedAt: string) => {
    const assignedDate = new Date(assignedAt)
    const now = new Date()
    const hoursSinceAssigned = (now.getTime() - assignedDate.getTime()) / (1000 * 60 * 60)
    
    if (hoursSinceAssigned > 48) return 'high'
    if (hoursSinceAssigned > 24) return 'medium'
    return 'low'
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default: return 'bg-green-100 text-green-800 border-green-200'
    }
  }

  const getUrgencyText = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'Hoog'
      case 'medium': return 'Gemiddeld'
      default: return 'Laag'
    }
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Toegang Geweigerd</h3>
          <p className="mt-1 text-sm text-gray-500">
            Alleen beheerders hebben toegang tot deze pagina.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Taken</h1>
          <p className="text-gray-600">Beheer vrijwilliger bevestigingen voor diensten</p>
        </div>
        <button
          onClick={fetchTodoItems}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Vernieuwen
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Totaal Items</p>
              <p className="text-2xl font-semibold text-gray-900">{todoItems.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Hoge Prioriteit</p>
              <p className="text-2xl font-semibold text-gray-900">
                {todoItems.filter(item => getUrgencyLevel(item.assignment.assigned_at) === 'high').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Vandaag Toegewezen</p>
              <p className="text-2xl font-semibold text-gray-900">
                {todoItems.filter(item => {
                  const assignedDate = new Date(item.assignment.assigned_at)
                  const today = new Date()
                  return assignedDate.toDateString() === today.toDateString()
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Todo List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
          <p className="mt-2 text-sm text-gray-500">Laden...</p>
        </div>
      ) : todoItems.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Alle taken voltooid!</h3>
          <p className="mt-1 text-sm text-gray-500">
            Er zijn momenteel geen diensten met toegewezen maar niet-bevestigde vrijwilligers.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {todoItems.map((item) => {
            const urgency = getUrgencyLevel(item.assignment.assigned_at)
            const isProcessing = processingIds.has(item.id)
            
            return (
              <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-start justify-between">
                  {/* Left side - Shift and Volunteer Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4">
                      {/* Volunteer Avatar */}
                      <div className="flex-shrink-0">
                        {item.volunteer.avatar_url ? (
                          <img
                            className="h-12 w-12 rounded-full"
                            src={item.volunteer.avatar_url}
                            alt={`${item.volunteer.first_name} ${item.volunteer.last_name}`}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-600" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {item.shift.title}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getUrgencyColor(urgency)}`}>
                            {getUrgencyText(urgency)}
                          </span>
                        </div>

                        {/* Volunteer Info */}
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-900">
                            {item.volunteer.first_name} {item.volunteer.last_name}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 mr-1" />
                              {item.volunteer.email}
                            </div>
                          </div>
                        </div>

                        {/* Shift Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2" />
                              {formatDayDateToLocal(item.shift.start_time, 'MMM d, yyyy')}
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-2" />
                              {formatTimeRangeToLocal(item.shift.start_time, item.shift.end_time)}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-2" />
                              {item.shift.location}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <Users className="h-4 w-4 mr-2" />
                              {item.shift.current_volunteers}/{item.shift.max_volunteers} vrijwilligers
                            </div>
                            {item.shift.event && (
                              <div className="text-gray-600">
                                Evenement: {item.shift.event.title}
                              </div>
                            )}
                            <div className="text-gray-500">
                              Toegewezen: {new Date(item.assignment.assigned_at).toLocaleString('nl-NL')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Actions */}
                  <div className="flex-shrink-0 ml-4">
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => fetchVolunteerDetails(item.assignment.volunteer_id)}
                        disabled={volunteerDetailsLoading}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {volunteerDetailsLoading ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Eye className="h-4 w-4 mr-2" />
                        )}
                        Details
                      </button>
                      <button
                        onClick={() => handleConfirmAssignment(item.id)}
                        disabled={isProcessing}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessing ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Bevestigen
                      </button>
                      <button
                        onClick={() => handleCancelAssignment(item.id)}
                        disabled={isProcessing}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Annuleren
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Volunteer Details Modal */}
      {isDetailsModalOpen && selectedVolunteer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsDetailsModalOpen(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Vrijwilliger Details
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsDetailsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {volunteerDetailsLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                    <p className="mt-2 text-sm text-gray-500">Laden...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Persoonlijke Informatie</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3">
                          {selectedVolunteer.profiles?.avatar_url ? (
                            <img
                              className="h-16 w-16 rounded-full"
                              src={selectedVolunteer.profiles.avatar_url}
                              alt={`${selectedVolunteer.profiles.first_name} ${selectedVolunteer.profiles.last_name}`}
                            />
                          ) : (
                            <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="h-8 w-8 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <p className="text-lg font-medium text-gray-900">
                              {selectedVolunteer.profiles?.first_name} {selectedVolunteer.profiles?.last_name}
                            </p>
                            <p className="text-sm text-gray-500">{selectedVolunteer.profiles?.email}</p>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600">
                          {selectedVolunteer.profiles?.phone && (
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 mr-2" />
                              {selectedVolunteer.profiles.phone}
                            </div>
                          )}
                          {selectedVolunteer.profiles?.city && (
                            <div>
                              <span className="font-medium">Stad:</span> {selectedVolunteer.profiles.city}
                            </div>
                          )}
                          {selectedVolunteer.profiles?.country && (
                            <div>
                              <span className="font-medium">Land:</span> {selectedVolunteer.profiles.country}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">Lid sinds:</span> {new Date(selectedVolunteer.profiles?.created_at).toLocaleDateString('nl-NL')}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Skills */}
                    {selectedVolunteer.skills && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Vaardigheden</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVolunteer.skills.map((skill: string, index: number) => (
                            <Tag key={index}>{skill}</Tag>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Shift History */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Dienst Geschiedenis</h4>
                      {selectedVolunteer.shiftHistory && selectedVolunteer.shiftHistory.length > 0 ? (
                        <div className="space-y-2">
                          {selectedVolunteer.shiftHistory.map((shift: any, index: number) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{shift.shifts.title}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDayDateToLocal(shift.shifts.start_time, 'MMM d, yyyy')} - {formatTimeRangeToLocal(shift.shifts.start_time, shift.shifts.end_time)}
                                </p>
                                <p className="text-xs text-gray-500">{shift.shifts.location}</p>
                              </div>
                              <div className="text-right">
                                <StatusBadge
                                  dimension="assignment"
                                  status={shift.status}
                                  size="sm"
                                  label={statusLabel('assignment', shift.status)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Geen dienst geschiedenis beschikbaar.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminTodoPage
